/* eslint-disable camelcase */
import { v4 as uuidv4 } from "uuid";
import { CONFIG } from "~/config";
import { deleteFromCache, updateMyReviewFromDeletedCache, hasDeletedReviewById } from "~/util/reviews-cache";
import { store } from "~/state/store";
import { REVIEW_DEBUG } from "~/review/const";
import { EventState } from "~/activerequest/interfaces";
import { IReview, IReviewItem, TStructuredData, ILocalityReview } from "~/review/interfaces";
import { IPoi } from "~/interfaces";
import { getActiveRequestOutput, getActiveRequests, getPutReviewOutput } from "./review-data";
import { detail } from "./poiagg";

const REVIEWS_URL = `${CONFIG.requestUrl}/reviews`;
const VERSION = "v1";
const LIST_REVIEW_LIMIT = 10;
let DEBUG_RESOLVE = 1;

if (REVIEW_DEBUG) {
	window.setReviewResolve = (state: 0 | 1) => {
		DEBUG_RESOLVE = state;
	};
}

interface IListReviewsOutput extends IReview {
	reviews: Array<IReviewItem>;
}

function queryParams(obj: object): string {
	return Object.keys(obj).map(key => `${key}=${encodeURIComponent(obj[key])}`).join("&");
}

function reviewFetch<T>(url: string, fetchOpts?: object): Promise<T> {
	return new Promise((resolve, reject) => {
		fetch(url, {
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
				"X-Correlation-Id": uuidv4(),
			},
			credentials: "include",
			...fetchOpts,
		}).then(request => {
			request.json().then(json => {
				if (request.status < 200 || request.status > 299 || (json.errors && json.errors.length)) {
					json.status = request.status;

					reject(json);
				} else {
					resolve(json);
				}
			}, error => {
				reject(error);
			});
		}, error => {
			reject(error);
		});
	});
}

function fakeFetch(data: any) {
	return Promise.resolve({
		status: 200,
		json: () => Promise.resolve(data),
	});
}

interface IPutReviewOutput extends IReview {
	review: IReviewItem;
}

export interface IPutReview {
	reviewSource?: string;
	source: string;
	poiId: number | string;
	text: string;
	mediaIds?: Array<number>;
	structuredRating?: TStructuredData;
	rating: number;
	activeRequest?: boolean;
	lang?: string;
}

// Review Add /v1/review POST
export function putReview({
	reviewSource = "mapy-web",
	source = "firm",
	poiId = 0,
	text = "",
	mediaIds = [],
	structuredRating = [],
	rating = 100,
	activeRequest = false,
	lang = CONFIG.defaultLang,
}: IPutReview): Promise<{
	review: IReview;
	myReview: IReviewItem;
}> {
	return new Promise((resolve, reject) => {
		const url = `${REVIEWS_URL}/${VERSION}/review?${queryParams({
			lang,
		})}`;
		const postData = {
			review_source: reviewSource,
			source,
			poi_id: poiId,
			text,
			media_ids: mediaIds,
			structured_rating: structuredRating,
			rating,
			active_request: activeRequest,
		};

		// smazeme, necheme posilat
		delete postData.active_request;

		if (REVIEW_DEBUG) {
			/* eslint-disable no-console */
			console.log(["put review", postData]);
			setTimeout(() => {
				const promOper = DEBUG_RESOLVE ? resolve : reject;
				const output = getPutReviewOutput({ reviewSource, source, poiId, text, mediaIds, structuredRating, rating, activeRequest, lang });

				promOper(output);
			}, 500);
		} else {
			reviewFetch<IPutReviewOutput>(url, {
				method: "POST",
				body: JSON.stringify(postData),
			}).then(json => {
				const myReview = json.review;

				delete json.review;
				resolve({
					review: json,
					myReview,
				});
			}, error => {
				/* eslint-disable no-console */
				console.log(error);

				reject(error);
			});
		}
	});
}

interface IPutReviewReply {
	reviewId: number;
	text: string;
	reviewSource?: string;
	lang?: string;
}

// Review Reply /v1/review/{review_id}/reply POST
export function putReviewReply({
	reviewId = 0,
	text = "",
	reviewSource = "mapy-web",
	lang = CONFIG.defaultLang,
}: IPutReviewReply) {
	return new Promise((resolve, reject) => {
		const url = `${REVIEWS_URL}/${VERSION}/review/${reviewId}/reply?${queryParams({
			lang,
		})}`;
		const replyData = {
			text,
			review_source: reviewSource,
		};

		if (REVIEW_DEBUG) {
			/* eslint-disable no-console */
			console.log(["put review reply", replyData]);
			setTimeout(() => {
				const promOper = DEBUG_RESOLVE ? resolve : reject;

				promOper({});
			}, 500);
		} else {
			reviewFetch(url, {
				method: "POST",
				body: JSON.stringify(replyData),
			}).then(json => {
				resolve(json);
			}, error => {
				/* eslint-disable no-console */
				console.log(error);

				reject(error);
			});
		}
	});
}

export type REVIEWS_ORDER = "latest" | "oldest" | "best" | "worst";

interface IGetUserReviews {
	offset?: number;
	limit?: number;
	order?: REVIEWS_ORDER;
	userPublicId: string;
	lang?: string;
	onlyRejected?: boolean;
}

// Review For User /v1/review GET vraci list se seznamem hodnoceni uzivatele
export function getUserReviews({
	offset = 0,
	limit = LIST_REVIEW_LIMIT,
	order = "latest",
	userPublicId = "",
	lang = CONFIG.defaultLang,
	onlyRejected = false,
}: IGetUserReviews): Promise<IListReviewsOutput> {
	return new Promise((resolve, reject) => {
		const url = `${REVIEWS_URL}/${VERSION}/review/user/${userPublicId}?${queryParams({
			offset,
			limit,
			order,
			lang,
			state: onlyRejected ? "rejected" : "all",
			forceTranslation: true,
		})}`;

		reviewFetch<IListReviewsOutput>(url).then(json => {
			resolve(json);
		}, error => {
			/* eslint-disable no-console */
			console.log(error);

			reject(error);
		});
	});
}

interface IReportReview {
	reviewId: number;
	reason: string;
	text: string;
	lang?: string;
}

// Review Report /v1/review/{review_id}/report PUT
export function reportReview({
	reviewId = 0,
	reason = "",
	text = "",
	lang = CONFIG.defaultLang,
}: IReportReview) {
	return new Promise((resolve, reject) => {
		const url = `${REVIEWS_URL}/${VERSION}/review/${reviewId}/report?${queryParams({
			lang,
		})}`;
		const reportData = {
			reason: reason || "",
			text: text || "",
		};

		if (REVIEW_DEBUG) {
			/* eslint-disable no-console */
			console.log(["report review", reportData]);
			setTimeout(() => {
				const promOper = DEBUG_RESOLVE ? resolve : reject;

				deleteFromCache(reviewId);
				promOper({});
			}, 500);
		} else {
			reviewFetch(url, {
				method: "PUT",
				body: JSON.stringify(reportData),
			}).then(json => {
				// pridame do smazane kese
				deleteFromCache(reviewId);
				resolve(json);
			}, error => {
				/* eslint-disable no-console */
				console.log(error);

				reject(error);
			});
		}
	});
}

interface IListReviews {
	source: string;
	id: number | string;
	offset?: number;
	limit?: number;
	order?: string;
	bestReviewsOnly?: boolean;
	filterByLang?: boolean;
	lang?: string;
	forceTranslation?: boolean;
}

// Review Poi List /v1/review/poi/{source}/{poi_id} GET
export function listReviews({
	source = "firm",
	id = 0,
	offset = 0,
	limit = LIST_REVIEW_LIMIT,
	order = "",
	bestReviewsOnly = false,
	filterByLang = false,
	lang = CONFIG.defaultLang,
	forceTranslation = true,
}: IListReviews): Promise<IListReviewsOutput> {
	return new Promise((resolve, reject) => {
		const url = `${REVIEWS_URL}/${VERSION}/review/poi/${source}/${id}?${queryParams({
			offset,
			limit,
			lang,
			bestReviewsOnly,
			filterByLang,
			forceTranslation,
			...order
				? {
					order: order,
				}
				: {},
		})}`;

		reviewFetch<IListReviewsOutput>(url).then(json => {
			// preskocime smazane
			const filtered = json.reviews.filter(review => !hasDeletedReviewById(review.id));
			const diff = json.reviews.length - filtered.length;

			json.reviews = filtered;
			json.total -= diff;
			resolve(json);
		}, error => {
			/* eslint-disable no-console */
			console.log(error);

			reject(error);
		});
	});
}

interface IDeleteReview {
	reviewId: number;
	reason: string|number;
	text: string;
	lang?: string;
}

// Review Delete /v1/review/{review_id} GET vraci 1 hodnoceni uzivatele
export function deleteReview({
	reviewId = 0,
	reason = "",
	text = "",
	lang = CONFIG.defaultLang,
}: IDeleteReview): Promise<Partial<IReview>> {
	return new Promise((resolve, reject) => {
		// u nefiremnich neposilat nic
		const deleteData = reason === 0
			? {}
			: {
				reason: reason || "",
				text: text || "",
			};
		const url = `${REVIEWS_URL}/${VERSION}/review/${reviewId}?${queryParams({
			lang,
		})}`;

		if (REVIEW_DEBUG) {
			/* eslint-disable no-console */
			console.log(["delete review", deleteData]);
			setTimeout(() => {
				const promOper = DEBUG_RESOLVE ? resolve : reject;

				deleteFromCache(reviewId);
				promOper({});
			}, 500);
		} else {
			reviewFetch(url, {
				method: "DELETE",
				body: JSON.stringify(deleteData),
			}).then(json => {
				deleteFromCache(reviewId);
				resolve(json);
			}, error => {
				/* eslint-disable no-console */
				console.log(error);

				reject(error);
			});
		}
	});
}

interface ICommentReview extends IReviewItem {
	seoHidden: boolean;
}

export interface IListReviewsCommentsOutput extends Omit<IListReviewsOutput, "reviews"> {
	reviews: Array<ICommentReview>;
	myReview?: ICommentReview;
	loadMore: boolean;
	offset: number;
}

interface IListReviewsComments extends IGetDetail {
	userId: number;
}

export function listReviewsComments({
	source = "",
	id = 0,
	userId = 0,
}: IListReviewsComments): Promise<IListReviewsCommentsOutput> {
	return new Promise((resolve, reject) => {
		const COUNT = 6;
		const DETAIL_COUNT = 3;

		// nacteme o 1 vic
		listReviews({
			source,
			id,
			offset: 0,
			limit: COUNT + 1,
		}).then(data => {
			let myReview: ICommentReview = null;

			const reviews = [];

			for (let ind = 0; ind < data.reviews.length; ind++) {
				const item: ICommentReview = {
					...data.reviews[ind],
					seoHidden: false,
				};

				if (userId === item.id) {
					myReview = item;

					continue;
				}

				reviews.push(item);

				// chceme pouze prvni 3, ostatni jsou seo skryte
				if (reviews.length > DETAIL_COUNT) {
					item.seoHidden = true;
				}

				// chceme pouze max 6 vysledku
				if (reviews.length === COUNT) {
					break;
				}
			}

			// vratime
			resolve({
				...data,
				reviews,
				myReview,
				// jestlize je to vice jak 3 vcetne me recenze, tak potrebujem detail pro vsechny recenze
				loadMore: data.reviews.length > DETAIL_COUNT,
				// nepotrebujeme
				offset: 0,
			});
		}, error => {
			reject(error);
		});
	});
}

interface IListReviewsPanelOutput extends IListReviewsOutput {
	loadMore: boolean;
	offset: number;
}

interface IListReviewsPanel extends IListReviews {
	userId: number;
	seoLoad: boolean;
}

export function listReviewsPanel({
	source = "",
	id = 0,
	offset = 0,
	order = "latest",
	userId = 0,
	seoLoad = false,
}: IListReviewsPanel): Promise<IListReviewsPanelOutput> {
	return new Promise((resolve, reject) => {
		const COUNT = seoLoad ? 20 : 10;

		// nacteme o 1 vic
		listReviews({
			source,
			id,
			offset,
			limit: COUNT,
			order,
		}).then(data => {
			const len = data.reviews.length;

			// odfiltrujeme uzivatelske recenze
			if (userId > 0) {
				data.reviews = data.reviews.filter(item => item.id !== userId && !hasDeletedReviewById(item.id));
			}

			// vratime
			resolve({
				...data,
				loadMore: len === COUNT && data.total > offset + COUNT,
				offset: offset + COUNT,
			});
		}, error => {
			reject(error);
		});
	});
}

export type IGetDetailOutput = {
	poi: IPoi;
	isOwner: boolean;
	review: IReview;
	myReview: IReviewItem;
};

interface IGetDetail {
	source: string;
	id: number | string;
	fetchPhotos?: boolean;
}

export function getDetail({
	source = "",
	id = 0,
	fetchPhotos = false,
}: IGetDetail): Promise<IGetDetailOutput> {
	return new Promise((resolve, reject) => {
		const options = {
			lang: CONFIG.langPriority,
			...fetchPhotos
				? {
					fetchPhoto: true,
					ratios: ["default", "1x1"],
					wikimedia: true,
				}
				: {},
		};

		detail(source, id, options).promise.then(data => {
			// pro firemni dvojcata
			if (data.poi.poilink && data.poi.poilink.value && data.poi.poilink.value.length) {
				const poiLinkValue = data.poi.poilink.value;

				for (let ind = 0; ind < poiLinkValue.length; ind++) {
					if (poiLinkValue[ind].review) {
						data.poi.review = poiLinkValue[ind].review;
						data.poi.review.id = poiLinkValue[ind].id;
						data.poi.review.source = poiLinkValue[ind].source;

						break;
					}
				}
			}

			// update dat
			updateMyReviewFromDeletedCache(data.poi);
			resolve({
				poi: data.poi,
				isOwner: data.poi.rus_admin,
				review: data.poi.review,
				myReview: data.poi.myReview,
			});
		}, error => {
			reject(error);
		});
	});
}

export function loginWithDetail({
	source = "",
	id = 0,
}: IGetDetail): Promise<IGetDetailOutput> {
	return new Promise((resolve, reject) => {
		const { user } = store.getState().user;

		function callGetDetail() {
			getDetail({
				source,
				id,
			}).then(data => {
				resolve(data);
			}, () => {
				resolve({
					poi: {},
					isOwner: false,
					review: {},
					myReview: null,
				});
			});
		}

		if (user.state === "green") {
			callGetDetail();
		} else {
			// musime se prihlasit
			Mapy.getComponent("login").open({
				analyticsData: {
					loginOrigin: "reviewsAdd",
					section: "reviews",
				},
				motivation: "review",
			}).then(() => {
				callGetDetail();
			}, () => {
				reject();
			});
		}
	});
}

export function updateReviewState(id: number, state: EventState) {
	if (REVIEW_DEBUG) {
		console.log(["updateReviewState", id, state]);

		return fakeFetch({ id, state });
	}

	return fetch(`${CONFIG.requestUrl}/events/v1/event/${id}/state`, {
		method: "PUT",
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
		body: JSON.stringify({
			state,
		}),
	});
}

export function fetchAlternativePOIs(eventId: number) {
	return fetch(`${CONFIG.requestUrl}/review-request/review/${eventId}/alternatives?flag=v3&lang=${CONFIG.defaultLang}`, {
		method: "GET",
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
	});
}

export function putConfirm() {
	if (REVIEW_DEBUG) {
		console.log(["putConfirm"]);

		return fakeFetch({});
	}

	return fetch(`${CONFIG.requestUrl}/events/v1/event/confirm?flag=v3`, {
		method: "PUT",
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
		body: JSON.stringify({
			all: true,
		}),
	});
}

export function fetchActiveRequests(params: Array<string> = []) {
	if (REVIEW_DEBUG) {
		console.log(["fetchActiveRequests"]);

		return fakeFetch(getActiveRequests());
	}

	return fetch(`${CONFIG.requestUrl}/events/v1/event?flag=v3&lang=${CONFIG.defaultLang}${params.length ? `&${params.join("&")}` : ""}`, {
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
			"Cache-Control": "no-cache",
		},
		credentials: "include",
	});
}

export function fetchActiveRequest(eventId: number) {
	if (REVIEW_DEBUG) {
		console.log(["fetchActiveRequest"]);

		return fakeFetch(getActiveRequestOutput({
			rrid: eventId,
		}));
	}

	return fetch(`${CONFIG.requestUrl}/events/v1/event/${eventId}?lang=${CONFIG.defaultLang}`, {
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
	});
}

export function setNotificationState(eventId: number, state: "deferred" | "dismissed" | "opened" | "never_this_poi") {
	if (REVIEW_DEBUG) {
		console.log(["setNotificationState", eventId, state]);

		return fakeFetch({ eventId, state });
	}

	return fetch(`${CONFIG.requestUrl}/review-request/notification/${eventId}`, {
		method: "PUT",
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
		body: JSON.stringify({ state }),
	});
}

interface IGetDetailPoisOutput {
	pois: Array<ILocalityReview>;
}

interface IGetDetailPois {
	source: string;
	id: number;
	lang?: string;
	forceTranslation?: boolean;
}

export function getDetailPois({
	source,
	id,
	lang = CONFIG.defaultLang,
	forceTranslation = false,
}: IGetDetailPois): Promise<IGetDetailPoisOutput> {
	const url = `${REVIEWS_URL}/${VERSION}/review/locality/${source}/${id}?${queryParams({
		lang,
		forceTranslation,
	})}`;

	return reviewFetch<IGetDetailPoisOutput>(url, {
		method: "GET",
	});
}
